import { useState } from 'react'
import { Star, Trash2, X, ExternalLink, ChevronDown, ChevronUp, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn, formatDateTime, isVideoUrl } from '@/lib/utils'
import type { Idea } from '@/types'

// Source display config (shared with IdeaCard)
const SOURCE_STYLES: Record<string, { label: string; className: string }> = {
  foreplay: { label: 'Ads Research', className: 'bg-amber-100 text-amber-700' },
  foreplay_ad_search: { label: 'Ad Search', className: 'bg-amber-100 text-amber-700' },
  foreplay_brand_ads: { label: 'Brand Ads', className: 'bg-amber-100 text-amber-700' },
  perplexity: { label: 'Web Research', className: 'bg-green-100 text-green-700' },
  perplexity_search: { label: 'Web Search', className: 'bg-green-100 text-green-700' },
  perplexity_sonar: { label: 'AI Chat', className: 'bg-green-100 text-green-700' },
  deep_research: { label: 'Deep Research', className: 'bg-emerald-100 text-emerald-700' },
  document: { label: 'Document Analysis', className: 'bg-blue-100 text-blue-700' },
  manual: { label: 'Manual', className: 'bg-gray-100 text-gray-700' },
}

const SENSITIVE_METADATA_KEY_PATTERNS = [
  /tool/i,
  /model/i,
  /provider/i,
  /foreplay/i,
  /perplexity/i,
  /deep_research/i,
  /o3/i,
]

function sanitizeMetadataText(text: string): string {
  return text
    .replace(/\bforeplay\b/gi, 'Ads Research')
    .replace(/\bperplexity\b/gi, 'Web Research')
    .replace(/\bdeep_research\b/gi, 'Deep Research')
    .replace(/o3[-_ ]?deep[-_ ]?research/gi, 'deep research model')
    .replace(/\bo3\b/gi, 'deep research model')
}

function sanitizeMetadataValue(value: unknown): unknown {
  if (typeof value === 'string') {
    return sanitizeMetadataText(value)
  }
  if (Array.isArray(value)) {
    return value.map((item) => sanitizeMetadataValue(item))
  }
  if (value && typeof value === 'object') {
    const sanitized: Record<string, unknown> = {}
    for (const [key, child] of Object.entries(value as Record<string, unknown>)) {
      if (SENSITIVE_METADATA_KEY_PATTERNS.some((pattern) => pattern.test(key))) {
        continue
      }
      sanitized[key] = sanitizeMetadataValue(child)
    }
    return sanitized
  }
  return value
}

function hasDisplayableMetadata(value: unknown): boolean {
  if (!value) return false
  if (Array.isArray(value)) return value.length > 0
  if (typeof value === 'object') return Object.keys(value as Record<string, unknown>).length > 0
  return true
}

interface IdeaDetailModalProps {
  idea: Idea | null
  open: boolean
  onClose: () => void
  onFavorite?: (idea: Idea) => void
  onDelete?: (idea: Idea) => void
  isDeleting?: boolean
  readOnly?: boolean
}

export function IdeaDetailModal({
  idea,
  open,
  onClose,
  onFavorite,
  onDelete,
  isDeleting = false,
  readOnly = false,
}: IdeaDetailModalProps) {
  const [metadataExpanded, setMetadataExpanded] = useState(false)
  const [expandedImage, setExpandedImage] = useState<string | null>(null)

  if (!open || !idea) return null

  const sourceStyle = SOURCE_STYLES[idea.source] || SOURCE_STYLES.manual
  const hasMedia = idea.media_urls && idea.media_urls.length > 0
  const sanitizedMetadata = sanitizeMetadataValue(idea.source_metadata)
  const hasMetadata = hasDisplayableMetadata(sanitizedMetadata)

  return (
    <>
      {/* Modal Overlay */}
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
        onClick={onClose}
      >
        <div
          className="relative bg-white rounded-2xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-start justify-between p-6 pb-4 border-b border-gray-100">
            <div className="flex-1 min-w-0 pr-4">
              <h2 className="text-xl font-semibold text-gray-900">{idea.title}</h2>
              <div className="flex items-center gap-2 mt-2">
                <span
                  className={cn(
                    'inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium',
                    sourceStyle.className
                  )}
                >
                  {sourceStyle.label}
                </span>
                {idea.source_url && (
                  <a
                    href={idea.source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 transition-colors"
                  >
                    <ExternalLink className="h-3 w-3" />
                    Source
                  </a>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 rounded-full hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* Description */}
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-2">Description</h3>
              <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">{idea.description}</p>
            </div>

            {idea.report_source && (
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-2">Report Source</h3>
                <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">{idea.report_source}</p>
              </div>
            )}

            {/* Tags */}
            {idea.tags.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-2">Tags</h3>
                <div className="flex flex-wrap gap-1.5">
                  {idea.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-2.5 py-1 rounded-full text-xs font-medium bg-gray-50 text-gray-600 border border-gray-200"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Media Gallery */}
            {hasMedia && (
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-2">
                  Media ({idea.media_urls.length})
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {idea.media_urls.map((url, idx) => {
                    const isVideo = isVideoUrl(url)
                    return (
                      <div
                        key={idx}
                        className="relative aspect-square bg-gray-100 rounded-lg overflow-hidden cursor-pointer group"
                        onClick={() => setExpandedImage(url)}
                      >
                        {isVideo ? (
                          <video
                            src={url}
                            className="w-full h-full object-cover"
                            preload="metadata"
                          />
                        ) : (
                          <img
                            src={url}
                            alt={`Media ${idx + 1}`}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                          />
                        )}
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                      </div>
                    )
                  })}
                </div>
              </div>
            )}

            {/* Usage Info */}
            <div className="flex items-center gap-6 text-sm">
              <div>
                <span className="text-gray-400">Used: </span>
                <span className={cn(
                  'font-medium',
                  idea.used_count > 0 ? 'text-green-700' : 'text-gray-500'
                )}>
                  {idea.used_count > 0 ? `${idea.used_count} time${idea.used_count > 1 ? 's' : ''}` : 'Never'}
                </span>
              </div>
              {idea.last_used_at && (
                <div>
                  <span className="text-gray-400">Last used: </span>
                  <span className="text-gray-700">{formatDateTime(idea.last_used_at)}</span>
                </div>
              )}
              <div>
                <span className="text-gray-400">Created: </span>
                <span className="text-gray-700">{formatDateTime(idea.created_at)}</span>
              </div>
            </div>

            {/* Source Metadata (Collapsible JSON) */}
            {hasMetadata && (
              <div className="border border-gray-200 rounded-lg">
                <button
                  onClick={() => setMetadataExpanded(!metadataExpanded)}
                  className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors rounded-lg"
                >
                  <span>Source Metadata</span>
                  {metadataExpanded ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                </button>
                {metadataExpanded && (
                  <div className="px-4 pb-4">
                    <pre className="text-xs text-gray-600 bg-gray-50 rounded-lg p-3 overflow-x-auto max-h-64 overflow-y-auto">
                      {JSON.stringify(sanitizedMetadata, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer Actions */}
          <div className="flex items-center justify-between p-6 pt-4 border-t border-gray-100">
            {readOnly ? (
              <>
                <div />
                <Button variant="outline" size="sm" onClick={onClose}>
                  Close
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onDelete?.(idea)}
                  disabled={isDeleting || !onDelete}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                >
                  {isDeleting ? (
                    <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4 mr-1.5" />
                  )}
                  {isDeleting ? 'Deleting...' : 'Delete'}
                </Button>

                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onFavorite?.(idea)}
                    disabled={!onFavorite}
                  >
                    <Star
                      className={cn(
                        'h-4 w-4 mr-1.5',
                        idea.is_favorited
                          ? 'fill-yellow-400 text-yellow-400'
                          : 'text-gray-400'
                      )}
                    />
                    {idea.is_favorited ? 'Unfavorite' : 'Favorite'}
                  </Button>
                  <Button variant="outline" size="sm" onClick={onClose}>
                    Close
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Expanded Image Overlay */}
      {expandedImage && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80"
          onClick={() => setExpandedImage(null)}
        >
          <button
            onClick={() => setExpandedImage(null)}
            className="absolute top-4 right-4 p-2 text-white hover:bg-white/20 rounded-full transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
          <div
            className="max-w-[90vw] max-h-[90vh] flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            {isVideoUrl(expandedImage) ? (
              <video
                src={expandedImage}
                className="max-w-full max-h-[90vh] rounded-lg"
                controls
                autoPlay
              />
            ) : (
              <img
                src={expandedImage}
                alt="Expanded"
                className="max-w-full max-h-[90vh] rounded-lg object-contain"
              />
            )}
          </div>
        </div>
      )}
    </>
  )
}
